import React, { useState, useEffect } from 'react';
import Layout from 'Component/Layouts/layout';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  Assessment,
  People,
  Schedule,
  TrendingUp,
  Download,
  Warning,
  PersonOff,
  Assignment,
  Group,
  Business,
  CalendarMonth,
  Receipt,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { clearReportData } from '../../reducer/trainingReportsSlice';
import { fetchTrainingReportData } from '../../thunks/trainingReportsThunks/thunk';
import { downloadAsExcel, formatDataForExcel } from '../../utils/excelUtils';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  fetchRegions,
  fetchAllTrainingTypes,
  fetchRegionsBranches,
} from '../../thunks/trainingThunks/thunk';

// Import modal components
import PendencyModal from './Modals/PendencyModal';
import AbsDnjResModal from './Modals/AbsDnjResModal';
import NominationModal from './Modals/NominationModal';
import TrainerWiseModal from './Modals/TrainerWiseModal';
import ClusterWiseModal from './Modals/ClusterWiseModal';
import MonthlyModal from './Modals/MonthlyModal';
import DateRangeModal from './Modals/DateRangeModal';

const TrainingReports = () => {
  const dispatch = useDispatch();
  const { loading, error, data } = useSelector((state) => state.trainingReportsSlice);
  const userData = useSelector((state) => state.authSlice.userData);
  const [loadingStates, setLoadingStates] = useState({});

  // Determine if the logged-in user is a basic trainer (no elevated roles)
  const userRoles = userData?.roles || [];
  const isBasicTrainer =
    userRoles.includes('TRAINER') &&
    !userRoles.includes('TRAINER_SUPER_ADMIN') &&
    !userRoles.includes('TRAINER_MANAGER') &&
    !userRoles.includes('TR_CLUSTER_MGR');

  // Modal state management
  const [activeModal, setActiveModal] = useState(null);
  const [modalData, setModalData] = useState({});

  // Get data from Redux
  const { availableRegions } = useSelector((state) => state.trainingSlice);
  const { availableBranches } = useSelector((state) => state.trainingSlicePrefetchData);
  const { trainingTypes } = useSelector((state) => state.trainingTypePageSlice.data);

  const availableReports = isBasicTrainer
    ? [
        'raw',
        'pendency',
        'absenteeism',
        'absdnjres',
        'nomination',
        'trainerwise',
        'monthly',
        'traininglog',
      ]
    : [
        'raw',
        'pendency',
        'absenteeism',
        'absdnjres',
        'nomination',
        'trainerwise',
        'clusterwise',
        'monthly',
        'traininglog',
      ];

  // Report configuration mapping
  const reportConfigs = {
    pendency: {
      modal: PendencyModal,
      requiresModal: true,
      payload: ['trainingType', 'regions', 'branches'],
    },
    absenteeism: {
      modal: DateRangeModal,
      requiresModal: true,
      payload: ['dateRange'],
    },
    absdnjres: {
      modal: AbsDnjResModal,
      requiresModal: true,
      payload: ['caseType', 'dateRange', 'regions'],
    },
    nomination: {
      modal: NominationModal,
      requiresModal: true,
      payload: ['trainingType', 'dateRange', 'regions'],
    },
    trainerwise: {
      modal: TrainerWiseModal,
      requiresModal: true,
      payload: ['trainingType', 'dateRange'],
    },
    clusterwise: {
      modal: ClusterWiseModal,
      requiresModal: true,
      payload: ['cluster', 'dateRange', 'trainingType'],
    },
    monthly: {
      modal: MonthlyModal,
      requiresModal: true,
      payload: ['month', 'year'],
    },
    raw: {
      modal: DateRangeModal,
      requiresModal: true,
      payload: ['dateRange'],
    },
    traininglog: {
      requiresModal: false,
    },
  };

  // Hardcoded report types
  const reportTypes = [
    {
      id: 'raw',
      title: 'Raw Report',
      description: 'Complete training analytics and insights',
      icon: <Assessment sx={{ fontSize: 40, color: '#9c27b0' }} />,
      color: '#f3e5f5',
      borderColor: '#9c27b0',
    },
    {
      id: 'pendency',
      title: 'Pendency Report',
      description: 'View pending training assignments and overdue completions',
      icon: <Warning sx={{ fontSize: 40, color: '#d32f2f' }} />,
      color: '#ffebee',
      borderColor: '#d32f2f',
    },
    {
      id: 'absenteeism',
      title: 'Absenteeism Report',
      description: 'Track employee attendance patterns and absenteeism trends',
      icon: <PersonOff sx={{ fontSize: 40, color: '#7b1fa2' }} />,
      color: '#f3e5f5',
      borderColor: '#7b1fa2',
    },
    {
      id: 'absdnjres',
      title: 'ABS/DNJ/RES Cases Report',
      description: 'Analyze absent, did not join, and resigned employee cases',
      icon: <Assignment sx={{ fontSize: 40, color: '#f57c00' }} />,
      color: '#fff3e0',
      borderColor: '#f57c00',
    },
    {
      id: 'nomination',
      title: 'Nomination Count Report',
      description: 'Track training nominations and enrollment statistics',
      icon: <Group sx={{ fontSize: 40, color: '#388e3c' }} />,
      color: '#e8f5e8',
      borderColor: '#388e3c',
    },
    {
      id: 'trainerwise',
      title: 'Trainer Wise Report',
      description: 'Performance analysis by individual trainers and their sessions',
      icon: <People sx={{ fontSize: 40, color: '#1976d2' }} />,
      color: '#e3f2fd',
      borderColor: '#1976d2',
    },
    {
      id: 'clusterwise',
      title: 'Cluster Wise Report',
      description: 'Training analytics organized by geographical clusters',
      icon: <Business sx={{ fontSize: 40, color: '#5d4037' }} />,
      color: '#efebe9',
      borderColor: '#5d4037',
    },
    {
      id: 'monthly',
      title: 'Monthly Training Report',
      description: 'Comprehensive monthly overview of all training activities',
      icon: <CalendarMonth sx={{ fontSize: 40, color: '#0277bd' }} />,
      color: '#e1f5fe',
      borderColor: '#0277bd',
    },
    {
      id: 'traininglog',
      title: 'Employee Training Log Report',
      description: 'Detailed log of all employee training activities',
      icon: <Receipt sx={{ fontSize: 40, color: '#388e3c' }} />,
      color: '#e8f5e8',
      borderColor: '#388e3c',
    },
  ];

  // Clear report data when component unmounts
  useEffect(() => {
    return () => {
      dispatch(clearReportData());
    };
  }, [dispatch]);

  // Fetch regions and training types on component mount
  useEffect(() => {
    dispatch(fetchRegions());
    dispatch(fetchAllTrainingTypes());
  }, [dispatch]);

  // Handle successful data fetch and download
  useEffect(() => {
    if (data.reportData && data.reportData.length > 0 && data.reportType) {
      try {
        const formattedData = formatDataForExcel(data.reportData, data.reportType);
        const fileName = `${data.reportType}_Training_Report`;

        downloadAsExcel(formattedData, fileName, data.reportType);

        toast.success(
          `${reportTypes.find((r) => r.id === data.reportType)?.title} downloaded successfully!`,
          {
            theme: 'colored',
          }
        );

        // Clear the data after successful download
        dispatch(clearReportData());
      } catch (error) {
        console.error('Error downloading report:', error);
        toast.error('Failed to download report', {
          theme: 'colored',
        });
      }
    }
  }, [data.reportData, data.reportType, dispatch]);

  // Generic report click handler
  const handleReportClick = async (reportType) => {
    console.log('Clicked report:', reportType.id);

    if (!availableReports.includes(reportType.id)) {
      toast.error(`${reportType.title} is not available yet.`, {
        theme: 'colored',
      });
      return;
    }

    const config = reportConfigs[reportType.id];
    console.log('Config for', reportType.id, ':', config);

    if (config.requiresModal) {
      console.log('Showing modal for:', reportType.id);
      // Show modal for reports that require configuration
      setActiveModal(reportType.id);
      setModalData({});
    } else {
      console.log('Direct API call for:', reportType.id);
      // Direct report generation for simple reports
      await generateReport(reportType.id);
    }
  };

  // Generic report generation function
  const generateReport = async (reportType, payload = {}) => {
    setLoadingStates((prev) => ({ ...prev, [reportType]: true }));
    console.log('payload', payload);
    try {
      // Dispatch the thunk to fetch report data with payload
      await dispatch(fetchTrainingReportData({ reportType, payload })).unwrap();
    } catch (error) {
      console.error('Error generating report:', error);
      toast.error(
        `Failed to generate ${reportTypes.find((r) => r.id === reportType)?.title}: ${error}`,
        {
          theme: 'colored',
        }
      );
    } finally {
      setLoadingStates((prev) => ({ ...prev, [reportType]: false }));
    }
  };

  // Modal handlers
  const handleModalClose = () => {
    setActiveModal(null);
    setModalData({});
  };

  const handleModalSubmit = async (reportType, payload) => {
    await generateReport(reportType, payload);
    handleModalClose();
  };

  // Render modal component
  const renderModal = () => {
    if (!activeModal) return null;

    const config = reportConfigs[activeModal];
    if (!config || !config.modal) return null;

    const ModalComponent = config.modal;
    const reportType = reportTypes.find((r) => r.id === activeModal);

    // For DateRangeModal, only pass basic props
    if (ModalComponent === DateRangeModal) {
      return (
        <ModalComponent
          open={!!activeModal}
          onClose={handleModalClose}
          onSubmit={(payload) => handleModalSubmit(activeModal, payload)}
          loading={loadingStates[activeModal]}
          reportType={reportType}
        />
      );
    }

    // For AbsDnjResModal and NominationModal, pass specific props
    if (ModalComponent === AbsDnjResModal || ModalComponent === NominationModal) {
      return (
        <ModalComponent
          open={!!activeModal}
          onClose={handleModalClose}
          onSubmit={(payload) => handleModalSubmit(activeModal, payload)}
          loading={loadingStates[activeModal]}
          reportType={reportType}
          availableRegions={availableRegions}
          availableBranches={availableBranches}
          trainingTypes={trainingTypes}
          dispatch={dispatch}
        />
      );
    }

    // For other modals, pass all props
    return (
      <ModalComponent
        open={!!activeModal}
        onClose={handleModalClose}
        onSubmit={(payload) => handleModalSubmit(activeModal, payload)}
        loading={loadingStates[activeModal]}
        reportType={reportType}
        availableRegions={availableRegions}
        availableBranches={availableBranches}
        trainingTypes={trainingTypes}
        dispatch={dispatch}
      />
    );
  };

  return (
    <Layout>
      <Box
        sx={{
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%)',
          p: { xs: 2, md: 3 },
        }}
      >
        {/* Professional Header */}
        <Box
          sx={{
            mb: 2,
            background: '#ffffff',
            borderRadius: '8px',
            p: 2,
            boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
            border: '1px solid #e2e8f0',
          }}
        >
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
            flexWrap="wrap"
            gap={1}
          >
            <Box>
              <Typography
                variant="h5"
                fontWeight="600"
                sx={{
                  color: '#1e293b',
                  mb: 0.25,
                }}
              >
                Training Reports
              </Typography>
              <Typography variant="body2" sx={{ color: '#64748b', fontWeight: '400' }}>
                Generate comprehensive reports and analytics for training data
              </Typography>
            </Box>
          </Box>
        </Box>

        {/* Error Alert */}
        {error && (
          <Alert
            severity="error"
            sx={{
              mb: 3,
              borderRadius: '12px',
              border: '1px solid #fecaca',
              backgroundColor: '#fef2f2',
            }}
          >
            {error}
          </Alert>
        )}

        {/* Reports Section */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid #e2e8f0',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
            mb: 4,
          }}
        >
          <CardContent sx={{ p: 4 }}>
            <Typography
              variant="h5"
              sx={{
                mb: 3,
                fontWeight: 700,
                color: '#1e293b',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Assessment sx={{ color: '#3b82f6' }} />
              Select a Report Type
            </Typography>

            <Grid container spacing={3}>
              {(isBasicTrainer
                ? reportTypes.filter((r) => r.id !== 'trainerwise' && r.id !== 'clusterwise')
                : reportTypes
              ).map((report) => (
                <Grid item xs={12} sm={6} md={3} key={report.id}>
                  <Card
                    sx={{
                      height: '100%',
                      cursor: availableReports.includes(report.id) ? 'pointer' : 'not-allowed',
                      transition: 'all 0.3s ease',
                      background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
                      border: `2px solid ${report.borderColor}`,
                      borderRadius: '16px',
                      opacity: availableReports.includes(report.id) ? 1 : 0.6,
                      boxShadow:
                        '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
                      '&:hover': availableReports.includes(report.id)
                        ? {
                            transform: 'translateY(-4px)',
                            boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
                            borderColor: report.borderColor,
                          }
                        : {},
                    }}
                    onClick={() =>
                      availableReports.includes(report.id) && handleReportClick(report)
                    }
                  >
                    <CardContent
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        textAlign: 'center',
                        padding: '24px',
                        height: '100%',
                      }}
                    >
                      <Box sx={{ mb: 2 }}>{report.icon}</Box>

                      <Typography
                        variant="h6"
                        sx={{
                          mb: 1,
                          fontWeight: 700,
                          color: report.borderColor,
                        }}
                      >
                        {report.title}
                      </Typography>

                      <Typography
                        variant="body2"
                        sx={{
                          mb: 3,
                          color: '#64748b',
                          flexGrow: 1,
                          lineHeight: 1.6,
                        }}
                      >
                        {report.description}
                      </Typography>

                      <Button
                        variant="contained"
                        startIcon={
                          loadingStates[report.id] ? (
                            <CircularProgress size={16} color="inherit" />
                          ) : (
                            <Download />
                          )
                        }
                        disabled={loadingStates[report.id] || !availableReports.includes(report.id)}
                        sx={{
                          backgroundColor: report.borderColor,
                          borderRadius: '12px',
                          fontWeight: '600',
                          textTransform: 'none',
                          boxShadow: `0 4px 14px 0 ${report.borderColor}40`,
                          '&:hover': {
                            backgroundColor: report.borderColor,
                            transform: 'translateY(-2px)',
                            boxShadow: `0 8px 25px 0 ${report.borderColor}60`,
                          },
                          '&:disabled': {
                            backgroundColor: '#e2e8f0',
                            color: '#94a3b8',
                            transform: 'none',
                            boxShadow: 'none',
                          },
                        }}
                      >
                        {loadingStates[report.id] ? 'Generating...' : 'Generate Report'}
                      </Button>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        {/* Information Section */}
        <Card
          sx={{
            background: 'linear-gradient(135deg, #ffffff 0%, #f8fafc 100%)',
            border: '1px solid #e2e8f0',
            borderRadius: '16px',
            boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)',
          }}
        >
          <CardContent sx={{ p: 3 }}>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: 700,
                color: '#1e293b',
                display: 'flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <Assignment sx={{ color: '#3b82f6' }} />
              Report Information
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: '#64748b',
                lineHeight: 1.6,
              }}
            >
              Click on any available report type above to generate the corresponding training
              report. Reports will be automatically downloaded in Excel format (.xlsx) with a
              timestamp. All report types are now available for download.
            </Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Render active modal */}
      {renderModal()}
    </Layout>
  );
};

export default TrainingReports;
